/**
 * testiConsegna — come la console dice la consegna del centralino (atlante cap. 264): i passi di «Assegna il numero
 * all'azienda», cosa succede il giorno della consegna, gli esiti e gli errori. Un posto solo per le parole, così la
 * scheda dell'azienda e la pagina «Da consegnare» dicono le stesse cose nello stesso modo.
 *
 * La console non ha l'i18n (è solo nostra, in italiano): le frasi stanno qui, come in `testiStrumenti.ts`. Le date
 * arrivano già scritte da chi chiama (`giorno`), così questo file resta puro e provabile.
 */

import type { SoftChipTone } from '../SoftChip';
import type {
  AllaConsegna,
  ConsegnaDelCentralino,
  ErroreDellaCallable,
  EsitoDelPasso,
  PassoAssegnazione,
} from '../../services/consegneService';

/** Scrive l'ULTIMO giorno di qualcosa che finisce all'istante ISO dato («fino al 31 ott»). */
export type ScriviGiorno = (iso: string) => string;

export const NOME_DEL_PASSO: Readonly<Record<PassoAssegnazione, string>> = {
  sottoaccount: 'Spazio Twilio dell’azienda',
  fascicolo: 'Fascicolo normativo',
  numero: 'Numero spostato',
  voce: 'Voce sul centralino',
  registro: 'Registro dei numeri',
};

/** La rilettura finale su Twilio, che il server segnala come passo `verifica` quando non torna. */
export const NOME_DELLA_VERIFICA = 'Verifica finale su Twilio';

/** Cosa farà ogni passo: l'anteprima mostrata PRIMA di confermare. */
export const COSA_FARA_IL_PASSO: Readonly<Record<PassoAssegnazione, string>> = {
  sottoaccount: 'Crea il sottoaccount Twilio dell’azienda, se non c’è già (lo stesso che usa WhatsApp).',
  fascicolo: 'Copia nel sottoaccount il fascicolo normativo approvato del numero, con il suo indirizzo.',
  numero: 'Sposta il numero dal conto principale al sottoaccount dell’azienda.',
  voce: 'Punta le chiamate in arrivo sul servizio del centralino.',
  registro: 'Registra il numero come numero vero dell’azienda: da quel momento il centralino risponde.',
};

export const ESITO_DEL_PASSO: Readonly<Record<EsitoDelPasso, { testo: string; tono: SoftChipTone }>> = {
  fatto: { testo: 'fatto', tono: 'success' },
  gia_fatto: { testo: 'già fatto', tono: 'default' },
  non_serve: { testo: 'non serve', tono: 'default' },
};

/**
 * Cosa succede il giorno della consegna, detto prima di premere: lo stesso calcolo del server (`viaDiAttivazione`).
 * `attenzione` quando il centralino NON si accenderebbe: allora conviene non comprare ancora il numero.
 */
export function fraseAllaConsegna(a: AllaConsegna | null, giorno: ScriviGiorno): { testo: string; attenzione: boolean } {
  if (!a) return { testo: 'Il centralino si accende e il canone parte oggi, dalla via di sempre.', attenzione: false };
  switch (a.via) {
    case 'senza_addebito':
      return { testo: 'Il centralino si accende senza addebito: l’azienda è gratuita per decisione.', attenzione: false };
    case 'nell_abbonamento':
      return a.gratis_fino_al
        ? { testo: `Il centralino si accende ed entra nell’abbonamento da oggi, gratis fino al ${giorno(a.gratis_fino_al)} (prova).`, attenzione: false }
        : { testo: 'Il centralino si accende ed entra nell’abbonamento da oggi: il canone parte oggi, in proporzione.', attenzione: false };
    case 'gratis_fino_a_fine_prova':
      return {
        testo: a.gratis_fino_al
          ? `Il centralino si accende gratis fino al ${giorno(a.gratis_fino_al)} (prova senza carta); poi serve l’abbonamento.`
          : 'Il centralino si accende gratis fino a fine prova; poi serve l’abbonamento.',
        attenzione: false,
      };
    case 'serve_il_pagamento':
      return {
        testo: 'L’azienda non ha un abbonamento né una prova in corso: il numero si assegnerebbe, ma il centralino resterebbe spento finché non sceglie il piano. Meglio non comprare ancora il numero.',
        attenzione: true,
      };
    case 'pagamento_in_sospeso':
      return { testo: 'C’è un pagamento in sospeso: il centralino non si accende finché l’azienda non lo sistema.', attenzione: true };
    case 'abbonamento_in_pausa':
      return { testo: 'L’abbonamento è in pausa: il centralino non si accende finché l’azienda non lo riattiva.', attenzione: true };
  }
}

/** Com'è andata la consegna, dopo l'assegnazione. */
export function fraseConsegnaFatta(c: ConsegnaDelCentralino, giorno: ScriviGiorno): string {
  const chiusa = c.richiesta_chiusa ? ' La richiesta dell’azienda è chiusa.' : '';
  if (c.esito === 'gia_consegnato') {
    const perche = { gia_acceso: 'era già acceso', piano: 'è già compreso nel piano dell’azienda', regalo: 'è regalato all’azienda' }[c.per];
    return `Il centralino ${perche}: niente di nuovo da addebitare.${chiusa}`;
  }
  const come =
    c.via === 'senza_addebito'
      ? 'senza addebito'
      : c.gratis_fino_al
        ? `gratis fino al ${giorno(c.gratis_fino_al)}`
        : c.via === 'gratis_fino_a_fine_prova'
          ? 'gratis fino a fine prova'
          : 'nell’abbonamento da oggi';
  return `Centralino acceso, ${come}.${chiusa}`;
}

const MOTIVI_DEL_RIFIUTO: Readonly<Record<string, string>> = {
  numero_di_altra_azienda: 'Questo numero risponde già per un’altra azienda: non si sposta.',
  assegnazione_a_meta_per_altra_azienda:
    'Un’assegnazione di questo numero a un’altra azienda si è fermata a metà: va chiusa prima di assegnarlo qui.',
  assegnazione_in_corso: 'Un’assegnazione di questo numero è già in corso: riprova fra qualche minuto.',
  numero_di_prova: 'È un numero del banco di prova: non diventa un numero vero.',
  numero_non_trovato: 'Twilio non trova questo numero sul conto principale: controlla di averlo scritto bene e che sia stato consegnato.',
  azienda_inesistente: 'L’azienda non esiste più.',
  richiesta_non_valida: 'La richiesta non è valida: controlla il numero.',
};

/**
 * L'errore di «Assegna il numero», detto per chi deve decidere cosa fare. `siPuoRilanciare`: i passi già fatti si
 * saltano, quindi rilanciare è sicuro.
 */
export function messaggioDiErrore(e: ErroreDellaCallable): { testo: string; siPuoRilanciare: boolean } {
  const d = e.dettagli;
  const rilanciabile = d.si_puo_rilanciare === true;
  if (d.passo === 'consegna') {
    const perche =
      d.motivo === 'serve_il_pagamento'
        ? 'l’azienda non ha un abbonamento né una prova in corso. Quando avrà scelto il piano, rilancia: i passi su Twilio si saltano e parte solo la consegna.'
        : e.messaggio;
    return { testo: `Il numero è assegnato, ma il centralino non si è acceso: ${perche}`, siPuoRilanciare: true };
  }
  if (typeof d.motivo === 'string' && MOTIVI_DEL_RIFIUTO[d.motivo]) {
    return { testo: MOTIVI_DEL_RIFIUTO[d.motivo], siPuoRilanciare: d.motivo === 'assegnazione_in_corso' };
  }
  if (typeof d.passo === 'string') {
    // `verifica` non è un passo dell'anteprima: è la rilettura finale del numero su Twilio, dopo i cinque passi.
    const nome = d.passo === 'verifica' ? NOME_DELLA_VERIFICA : ((NOME_DEL_PASSO as Record<string, string>)[d.passo] ?? d.passo);
    const twilio = typeof d.codice_twilio === 'string' || typeof d.codice_twilio === 'number' ? ` (Twilio ${d.codice_twilio})` : '';
    return { testo: `Il passo «${nome}» non è riuscito${twilio}: ${e.messaggio}`, siPuoRilanciare: rilanciabile };
  }
  return { testo: e.messaggio, siPuoRilanciare: rilanciabile };
}

/**
 * Un numero scritto in modo plausibile: solo cifre, spazi e separatori, almeno 8 cifre. La forma internazionale
 * (E.164) la decide il server, che restituisce il numero normalizzato nel risultato.
 */
export function numeroPlausibile(scritto: string): boolean {
  const s = scritto.trim();
  if (!/^\+?[0-9][0-9 ()./-]*$/.test(s)) return false;
  return s.replace(/\D/g, '').length >= 8;
}

/** Il canale, detto come lo dice chi ha chiesto: a mano o tramite Yuma. */
export function comeHaChiesto(via: 'mano' | 'assistente_ai'): string {
  return via === 'assistente_ai' ? 'chiesto a Yuma' : 'dalla pagina';
}

// ============================================================================
// GLI STRUMENTI A CANONE DALLA SCHEDA «CATALOGO & ADD-ON»
// ============================================================================

/**
 * Cosa ha fatto `setTenantAddons` su uno strumento a canone (`strumento_a_canone` della risposta): dal 10/10/2026 non
 * scrive più l'addon a mano ma passa dal percorso unico (`cambiaStatoStrumento`), come la vetrina: azienda gratuita
 * senza addebito, in prova gratis fino a fine prova, altrimenti nell'abbonamento.
 */
export interface EsitoStrumentoACanone {
  stato: 'acceso' | 'spento' | 'gia_cosi' | 'serve_il_pagamento' | 'in_attesa_di_consegna' | 'richiesta_annullata';
  via?: string;
  gratis_fino_al?: string | null;
}

export function messaggioEsitoStrumento(
  etichetta: string,
  esito: EsitoStrumentoACanone,
  giorno: ScriviGiorno,
): { testo: string; gravita: 'success' | 'info' | 'warning' } {
  switch (esito.stato) {
    case 'acceso': {
      const come =
        esito.via === 'senza_addebito'
          ? 'senza addebito (azienda gratuita)'
          : esito.gratis_fino_al
            ? `gratis fino al ${giorno(esito.gratis_fino_al)}`
            : 'nell’abbonamento da oggi';
      return { testo: `${etichetta} acceso, ${come}.`, gravita: 'success' };
    }
    case 'spento':
      return { testo: `${etichetta} spento.`, gravita: 'success' };
    case 'gia_cosi':
      return { testo: `${etichetta}: era già così.`, gravita: 'info' };
    case 'serve_il_pagamento':
      return { testo: `${etichetta} non si è acceso: l’azienda non ha un abbonamento né una prova in corso.`, gravita: 'warning' };
    case 'in_attesa_di_consegna':
      return { testo: `${etichetta}: richiesta aperta, si accende con la sua consegna.`, gravita: 'info' };
    case 'richiesta_annullata':
      return { testo: `${etichetta}: la richiesta che aspettava la consegna è annullata.`, gravita: 'info' };
  }
}

/**
 * Il rifiuto di `setTenantAddons` per uno strumento che si accende solo con la sua consegna (il centralino): niente
 * interruttore, si usa l'operazione che lo rende funzionante. `null` se il rifiuto è un altro.
 */
export function messaggioStrumentoDaConsegnare(e: ErroreDellaCallable, etichetta: string): string | null {
  if (e.dettagli.codice !== 'strumento_da_consegnare') return null;
  return `${etichetta} si accende con la sua consegna, non dall’interruttore: per il centralino è «Assegna il numero all’azienda», nella scheda Strumenti. Il canone parte quel giorno.`;
}
